// mobile-sync-bootstrap — liga o sync cross-device na superfície MOBILE.
//
// Até 10/10/2026 o celular não tinha sync NENHUM: o login por e-mail/Google persistia a
// sessão Swipe, mas o store nunca recebia a fonte (providerCode + usuário), então o
// `syncPull` saía cedo, o `savePosition` não gravava e o "Continuar" voltava vazio. E os
// perfis eram só locais: o celular via lista vazia e criava um perfil novo (o P2
// duplicado da conta do dono nasceu 54 s depois do 1º login de um Android).
//
// Roda no login, no boot com sessão e depois de salvar a fonte:
//   1. hidrata o contexto pela fonte da conta (account-context.ts, a MESMA sequência da TV);
//   2. puxa os perfis da conta (adota os uuids do servidor, como a TV);
//   3. decide o perfil ativo pela regra do dono (`decideMobileProfile`): mantém, adota,
//      ou PERGUNTA uma vez (a folha "Quem está assistindo?", via `useMobileSyncUi`);
//   4. com perfil adotado, puxa os itens dele.
// Nunca lança: é tudo best-effort, e o app funciona local se a rede falhar.
//
// ── A EPOCH (cybersec P1, caminhos b e c) ──
// O bootstrap tem vários `await`. Um logout no meio (ou a sessão trocando de conta) não
// pode ser desfeito por ele: antes, a resposta que chegava depois da limpeza regravava
// a conta e o perfil de quem saiu, inclusive dentro da janela do flush do `endSwipeSync`
// (a geração da sessão ainda não tinha mudado ali). Cada execução guarda a epoch e a
// conta da sessão do começo, e confere as duas depois de CADA `await` e antes de cada
// gravação. O logout incrementa a epoch (`bumpMobileSyncEpoch`) ANTES de qualquer limpeza.

import { create } from 'zustand'

import { hydrateMobileSourceContext, type MobileHydrateOutcome } from '../account-context'
import { decideMobileProfile } from '../profiles-gate'
import { loadAccountProfiles } from '../profiles-load'
import { accountKey, useSessionStore } from '../store'
import { getSwipeUserId, hasSwipeSession, syncPull } from '../sync'

export interface MobileSyncBootstrapResult {
  hydrate: MobileHydrateOutcome
  /** `kept`: o perfil ativo já era da conta. `adopted`: um perfil da conta virou o
   *  ativo agora. `ask`: a escolha é do usuário (a folha abre). `none`: sem perfil da
   *  conta (ou o pull falhou). `cancelled`: logout/troca de conta no meio. */
  profile: 'kept' | 'adopted' | 'ask' | 'none' | 'cancelled'
}

/**
 * Estado de UI que o bootstrap entrega ao shell: `ask` = abrir a folha "Quem está
 * assistindo?" (uma vez; o shell volta pra `none` ao fechar).
 */
export const useMobileSyncUi = create<{ prompt: 'none' | 'ask'; accountLoadFailed: boolean }>(() => ({
  prompt: 'none',
  accountLoadFailed: false,
}))

let epoch = 0

/** O logout (ou a troca de conta) invalida todo bootstrap em voo. Chamar ANTES da limpeza. */
export function bumpMobileSyncEpoch(): void {
  epoch += 1
  useMobileSyncUi.setState({ prompt: 'none', accountLoadFailed: false })
}

let inflight: Promise<MobileSyncBootstrapResult> | null = null
let next: Promise<MobileSyncBootstrapResult> | null = null

/**
 * Uma execução por vez. Quem chama DURANTE uma execução ganha uma execução NOVA logo
 * depois dela (e não o resultado dela): a fonte pode ter acabado de mudar (salvar a
 * fonte enquanto o bootstrap do boot ainda roda), e o resultado em voo seria o da
 * fonte velha. Vários chamadores no meio da mesma execução compartilham a seguinte.
 */
export function bootstrapMobileSync(): Promise<MobileSyncBootstrapResult> {
  if (!inflight) return start()
  if (!next) {
    next = inflight.then(() => {
      next = null
      return start()
    })
  }
  return next
}

function start(): Promise<MobileSyncBootstrapResult> {
  const run = runBootstrap().catch((): MobileSyncBootstrapResult => ({ hydrate: 'failed', profile: 'none' }))
  inflight = run
  const release = (): void => {
    if (inflight === run) inflight = null
  }
  void run.then(release, release)
  return run
}

const CANCELLED: MobileSyncBootstrapResult = { hydrate: 'cancelled', profile: 'cancelled' }

async function runBootstrap(): Promise<MobileSyncBootstrapResult> {
  const myEpoch = epoch
  const myUser = getSwipeUserId()
  const cancelled = (): boolean => epoch !== myEpoch || myUser === null || getSwipeUserId() !== myUser
  if (cancelled()) return CANCELLED

  const hydrate = await hydrateMobileSourceContext({ isCancelled: cancelled })
  if (cancelled()) return CANCELLED
  // A fonte da conta não pôde ser lida e o store ficou SEM contexto (o da conta anterior
  // caiu com a sessão dela): o shell mostra o erro com "Tentar novamente" em vez de uma
  // Home vazia sem explicação. Com contexto de um boot anterior, o app segue com ele.
  useMobileSyncUi.setState({ accountLoadFailed: hydrate === 'failed' && accountKey(useSessionStore.getState()) === null })
  const account = accountKey(useSessionStore.getState())
  if (!account || !hasSwipeSession()) return { hydrate, profile: 'none' }

  // Puxa os perfis da conta (e, com perfil ativo, os itens dele, no mesmo syncPull).
  const load = await loadAccountProfiles(account)
  if (cancelled()) return CANCELLED
  if (load.pull !== 'ok' || !load.remoteIds) return { hydrate, profile: 'none' }

  const s = useSessionStore.getState()
  const decision = decideMobileProfile(load.profiles, load.remoteIds, s.activeProfileId, s.lastProfileIdByAccount[account])
  if (decision.kind === 'keep') return { hydrate, profile: 'kept' }
  if (decision.kind === 'none') return { hydrate, profile: 'none' }
  if (decision.kind === 'ask') {
    useMobileSyncUi.setState({ prompt: 'ask' })
    return { hydrate, profile: 'ask' }
  }
  // Última conferência antes de gravar: entre o pull e aqui a conta pode ter saído.
  if (cancelled()) return CANCELLED
  s.setActiveProfile({ id: decision.profile.id, kind: decision.profile.kind })
  // Os itens do perfil adotado ("continuar de onde parou" vindo da TV).
  await syncPull()
  return { hydrate, profile: 'adopted' }
}
